import React, {useState} from 'react'
import './Contact.css';

import Header from '../../sections/Header/Header';
import Footer from '../../sections/Footer/Footer';
import SEO from '../../components/SEO/SEO';

import { submitContact } from '../../../services/contactService';

const initialForm = { name:'', email:'', phone:'', message:'' };

const validateField = (field, value) => {
    const trimmedValue = value.trim();

    if (field === 'name' && !trimmedValue) return 'Please enter your name.';
    if (field === 'email' && !trimmedValue) return 'Please enter your email address.';
    if (field === 'email' && !/^\S+@\S+\.\S+$/.test(trimmedValue)) return 'Please enter a valid email address.';
    if (field === 'message' && !trimmedValue) return 'Please enter a message.';

    return '';
};

const validateForm = (form) => Object.keys(form).reduce((nextErrors, field) => {
    const error = validateField(field, form[field]);
    if (error) nextErrors[field] = error;
    return nextErrors;
}, {});

const Contact = () => {

    const [form, setForm] = useState(initialForm)
    const [errors, setErrors] = useState({})
    const [submissionError, setSubmissionError] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const handleChange = (field, value) => {
        setForm(prev => ({...prev, [field]: value}))
        setSubmissionError('')
        if (errors[field]) {
            setErrors(prev => ({...prev, [field]: validateField(field, value)}))
        }
    }

    const handleBlur = (field) => {
        const error = validateField(field, form[field]);
        setErrors(prev => ({...prev, [field]: error}))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const nextErrors = validateForm(form)
        setErrors(nextErrors)
        setSubmissionError('')

        if (Object.keys(nextErrors).length > 0) return

        setSubmitting(true)
        try {
            await submitContact({
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                message: form.message.trim()
            })
            setSubmitted(true)
        } catch (error) {
            setSubmissionError(error instanceof TypeError
                ? 'Unable to connect to the contact service. Please try again.'
                : error.message || 'We could not send your message. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <SEO title="Contact • Adult Entertainment Agency Italy" description="Get in touch with SADIDA Network. Elite escort and fantasy agency serving Rome, Milan, and all of Italy. Enquiries answered within 24 hours." />
            <Header />
            <section className="section-header contact-page">
                <div className="view-container">
                    <h1>Contact</h1>
                    <p className="contact-intro">Get in touch with our team. We typically respond within 24 hours.</p>

                    {submitted ? (
                        <div className="contact-success" role="status" aria-live="polite">
                            <p>Thank you for your message. We'll be in touch shortly.</p>
                        </div>
                    ) : (
                        <form className="contact-form" onSubmit={handleSubmit} noValidate>
                            {submissionError && <p className="contact-error" role="alert">{submissionError}</p>}

                            <div className="contact-field">
                                <label htmlFor="contact-name">Name</label>
                                <input
                                    id="contact-name"
                                    name="name"
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    onBlur={() => handleBlur('name')}
                                    autoComplete="name"
                                    aria-invalid={Boolean(errors.name)}
                                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                                    required
                                />
                                {errors.name && <p className="contact-field-error" id="contact-name-error">{errors.name}</p>}
                            </div>
                            <div className="contact-field">
                                <label htmlFor="contact-email">Email</label>
                                <input
                                    id="contact-email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    onBlur={() => handleBlur('email')}
                                    autoComplete="email"
                                    aria-invalid={Boolean(errors.email)}
                                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                                    required
                                />
                                {errors.email && <p className="contact-field-error" id="contact-email-error">{errors.email}</p>}
                            </div>
                            <div className="contact-field">
                                <label htmlFor="contact-phone">Phone <span>(optional)</span></label>
                                <input
                                    id="contact-phone"
                                    name="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    autoComplete="tel"
                                    placeholder="+39"
                                />
                            </div>
                            <div className="contact-field">
                                <label htmlFor="contact-message">Message</label>
                                <textarea
                                    id="contact-message"
                                    name="message"
                                    value={form.message}
                                    onChange={(e) => handleChange('message', e.target.value)}
                                    onBlur={() => handleBlur('message')}
                                    rows="5"
                                    aria-invalid={Boolean(errors.message)}
                                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                                    required
                                />
                                {errors.message && <p className="contact-field-error" id="contact-message-error">{errors.message}</p>}
                            </div>
                            <button className="contact-submit" type="submit" disabled={submitting}>
                                {submitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    )}
                </div>
            </section>
            <Footer />
        </>
    )
}

export default Contact
